/* eslint-disable class-methods-use-this */
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngxs/store';
import { Observable, Subscription, map, take } from 'rxjs';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { UserDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { APILoadingStatus } from 'src/app/common/types/types';
import { Animations } from 'src/app/common/utilities/animations';
import { BooleanNumber } from 'src/app/common/utilities/enums/boolean-number.enum';
import { select } from 'src/app/common/utilities/ngxs-utils';
import { checkIfE2L } from 'src/app/common/utilities/role-helpers';
import {
  checkIfCoach,
  checkIfCoachee,
  checkIfCreator,
  checkIfShadower,
  checkPresentAttendee,
} from 'src/app/common/utilities/session-helpers';
import {
  getPreviousFormId,
  getSavedFormData,
  hasSavedFormData as hasStoredFormData,
} from 'src/app/private/shared/components/forms/view/view-field/ form-view.helpers';
import { ViewFormComponent } from 'src/app/private/shared/components/forms/view/view-form/view-form.component';
import { ModalComponent } from 'src/app/private/shared/components/modals/modal/modal.component';
import {
  CoachingSessionAttendeeDTO,
  CoachingSessionDTO,
} from 'src/app/private/shared/dtos/coaching-session.dto';
import {
  CompetencyFieldOutput,
  Form,
  FormLearnerBehaviorCreateDTO,
  FormStatus,
  FormSubmissionStatus,
  ObservationFormSubmission,
  ObservationFormSubmissionRawData,
} from 'src/app/private/shared/dtos/forms.dto';
import { ProformViewStates } from 'src/app/private/shared/dtos/proforms.dto';
import { checkSessionDataType } from 'src/app/private/shared/helpers/coachee-log.utilities';
import { AlertService } from 'src/app/private/shared/services/alert/alert.service';
import { CoachingLogService } from 'src/app/private/shared/services/coaching-log/coaching-log.service';
import { DistrictListService } from 'src/app/private/shared/services/district-list/district-list-service';
import { FormsSaveService } from 'src/app/private/shared/services/forms/forms-save.service';
import { UserPusherService } from 'src/app/private/shared/services/pusher/user.pusher.service';
import { FEATURE_FLAGS } from 'src/app/private/shared/services/unleash/unleash.helpers';
import { UnleashService } from 'src/app/private/shared/services/unleash/unleash.service';
import { FetchSessionData } from 'src/app/private/shared/state/coaching-log/coaching-log.actions';
import { CoachingLogState } from 'src/app/private/shared/state/coaching-log/coaching-log.state';
import { ObserveeViewPayloadItem } from 'src/app/private/shared/types/payloads/coaching-log.payloads';
import { ViewLogAPIResponse } from 'src/app/private/shared/types/responses/coaching-log.responses';
import {
  GetAllProFormsAPIResponse,
  ProFormContext,
  ProFormStatus,
  ProFormType,
  ProFormsAPIResponse,
  ProFormsSubmissionAPIResponse,
} from 'src/app/private/shared/types/responses/proforms.responses';
import { FormsService } from '../../../shared/services/forms/forms.service';
import { ProformsFormListService } from '../../../shared/services/proforms/proforms-formlist.service';
import { ProformsSubmissionService } from '../../../shared/services/proforms/proforms-submission.service';

// Type for forms that can be either Form or ProForm
type FormOrProForm = Form | ProFormsAPIResponse;

// Helper function to check if a form is a ProForm
function isProForm(form: FormOrProForm): form is ProFormsAPIResponse {
  return 'type' in form && 'creator_user_id' in form;
}

// Helper function to check if a form is a regular Form
function isObservationForm(form: FormOrProForm): form is Form {
  return 'createdAt' in form && 'author' in form;
}

@Component({
  selector: 'app-observation-form',
  templateUrl: './observation-form-page.component.html',
  styleUrls: ['./observation-form-page.component.scss'],
  standalone: false,
  animations: [Animations.fadeInOut],
})
export class ObservationFormPageComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  SESSION_STORAGE_KEY: string;

  LAST_FORM_KEY = 'last-form-id';

  lastFormId: number;

  @ViewChild('formView') formView: ViewFormComponent;

  @ViewChild('formSelect') formSelect: NgSelectComponent;

  sessionData$ = select(CoachingLogState.getSessionData);

  sessionData: CoachingSessionDTO;

  coachLogStatus$: Observable<APILoadingStatus> = select(
    CoachingLogState.getCoachLogStatus
  );

  coachLogStatus: APILoadingStatus;

  disableLockFormButton = true;

  sessionLoaded = false;

  attendeeUsers: UserDTO[] = [];

  hasPresentAttendee = false;

  isCoach = false;

  isCreator: boolean;

  isShadower = false;

  isCoachee = false;

  isAdmin = false;

  formDataLoading = true;

  loadStatus = {
    forms: false,
    sessionData: false,
    submissions: false,
    proforms: false,
  };

  user: User;

  isE2L = false;

  saveError = false;

  sessionId: number;

  formsList: FormOrProForm[];

  selectedAttendee: CoachingSessionAttendeeDTO;

  selectedForm: FormOrProForm | null = null;

  formSelectValue: FormOrProForm;

  indicatorDeltas: CompetencyFieldOutput[] = [];

  @ViewChild('saveWarningModal') saveWarningModal: ModalComponent;

  @ViewChild('unloadedSavedDataModal') unloadedSavedDataModal: ModalComponent;

  formLocked = false;

  submissions: ObservationFormSubmission[] = [];

  SubmissionStatuses = FormSubmissionStatus;

  submissionStatus: FormSubmissionStatus = FormSubmissionStatus.NEW;

  createSubmissionData: ObservationFormSubmissionRawData;

  currentSubmission: ObservationFormSubmission;

  formSaving = false;

  smartFeedbackActive = false;

  smartFeedbackTabActive = true;

  notesActive = false;

  notesTabActive = true;

  commentsActive = false;

  commentsTabActive = true;

  resourcesActive = false;

  observationProFormsEnabled = false;

  resourcesTabActive = true;

  apiUrl = EnvironmentService.apiUrl();

  subs: Subscription[] = [];

  ProformViewStates = ProformViewStates;

  ProFormContext = ProFormContext;

  proformSubmissions: ProFormsSubmissionAPIResponse[] = [];

  currentProformSubmission: ProFormsSubmissionAPIResponse | null = null;

  proformSubmissionsLoaded = false;

  participantView?: ViewLogAPIResponse;

  districtsEmailNotificationsEnabled: BooleanNumber;

  processingNotification = false;

  constructor(
    private route: ActivatedRoute,
    private formsService: FormsService,
    private store: Store,
    private router: Router,
    private userPusherService: UserPusherService,
    protected formsSaveService: FormsSaveService,
    private featureFlagService: UnleashService,
    private proformsFormListService: ProformsFormListService,
    public coachingService: CoachingLogService,
    private proformsSubmissionService: ProformsSubmissionService,
    private districtListService: DistrictListService,
    private alertServce: AlertService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.isE2L = checkIfE2L(this.user);
    this.observationProFormsEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.observationProForms
    );
  }

  ngOnInit(): void {
    this.route.params.subscribe((url) => {
      // Reset any cached ProForm submission state when entering or re-entering the observation form page so that
      // child ProForm components don't see stale submissions from a previous visit.
      this.proformsSubmissionService.resetCurrentSubmission();
      this.proformsSubmissionService.resetNewSubmission();
      this.proformSubmissions = [];
      this.currentProformSubmission = null;

      this.unsetForm();
      this.formLocked = false;

      this.sessionId = parseInt(url['logId']);
      this.SESSION_STORAGE_KEY = `observation-form-${this.sessionId}`;
      this.store.dispatch(new FetchSessionData(this.sessionId));

      this.formsService.isLiveFormSubmission = true;
      this.formsService.setCoachlog(this.sessionId);

      this.sessionData$
        .pipe(
          map((filterFn) =>
            (filterFn as (id: number) => CoachingSessionDTO)(this.sessionId)
          )
        )
        .pipe(
          map((sessionData) => {
            if (sessionData) {
              this.sessionData = sessionData;
              checkSessionDataType(sessionData, 'observation', this.router);

              let observationFormDistrictId = this.sessionData.district.id;

              this.sessionData.attendees = this.sessionData.attendees.map(
                (attendee) => {
                  this.userPusherService.addChannel(
                    `growth-${attendee.userId}`
                  );
                  if (attendee.user.district_id) {
                    observationFormDistrictId = attendee.user.district_id;
                  }
                  return {
                    ...attendee,
                    fullName: `${attendee.user.profile.first_name} ${attendee.user.profile.last_name}`,
                  };
                }
              );
              if (this.observationProFormsEnabled) {
                // If ProForms are enabled, only load ProForms
                this.getObservationProForms(observationFormDistrictId);
                // Mark regular forms as loaded since we're not using them
                this.loadStatus.forms = true;
              } else {
                this.formsService
                  .getFormSubmissionsFromCoachingLog(this.sessionId)
                  .subscribe((submissions) => {
                    this.submissions = submissions;
                    this.loadStatus.submissions = true;
                    this.checkDataLoaded();
                  });
                // If ProForms are disabled, only load regular forms
                this.formsService
                  .loadForms(
                    observationFormDistrictId,
                    1000,
                    1,
                    undefined,
                    FormStatus.PUBLISHED
                  )
                  .subscribe(([forms]) => {
                    this.formsList = forms;
                    this.loadStatus.forms = true;
                    // Mark proforms as loaded since we're not using them
                    this.loadStatus.proforms = true;
                    this.checkDataLoaded();
                  });
              }

              this.loadStatus.sessionData = true;
              this.hasPresentAttendee = checkPresentAttendee(this.sessionData);
              this.isCoach = checkIfCoach(this.sessionData);
              this.isCreator = checkIfCreator(this.sessionData, this.user.id);
              this.isShadower = checkIfShadower(this.sessionData);
              this.isAdmin =
                this.user.roles.includes(UserRole.E2L_EMPLOYEE) ||
                this.user.roles.includes(UserRole.DISTRICT_ADMIN);
              this.isCoachee = checkIfCoachee(this.sessionData);
              this.checkDataLoaded();
            }
          })
        )
        .subscribe();
      this.coachLogStatus$.subscribe((status) => {
        if (status.loaded) {
          this.coachLogStatus = status;
          this.sessionLoaded = true;
        }
      });
      this.subs.push(
        this.formsService.promptSave.subscribe((save) => {
          if (save) {
            this.submitForm();
          }
        })
      );
    });
  }

  ngAfterViewInit(): void {
    this.subs.push(
      this.formsSaveService.promptUnsavedModal.subscribe((sessionId) => {
        if (sessionId === this.sessionId) {
          this.saveWarningModal.open();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.formsSaveService.clearNavigationQueue(this.sessionId);
    this.formsService.isLiveFormSubmission = false;
    this.participantView = undefined;
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  checkDataLoaded() {
    if (
      this.loadStatus.forms &&
      this.loadStatus.sessionData &&
      (this.loadStatus.submissions || this.loadStatus.proforms)
    ) {
      if (!this.isCoachee) {
        this.attendeeSelected(this.sessionData.attendees[0]);
        if (this.selectedAttendee.user.district_id) {
          this.checkDistrictsEmailNotificationStatus(
            this.selectedAttendee.user.district_id
          );
        }
      } else {
        const attendeeIdx = this.sessionData.attendees.findIndex(
          (attendee) => attendee.userId === this.user.id
        );
        if (attendeeIdx > -1) {
          this.attendeeSelected(this.sessionData.attendees[attendeeIdx]);
        }
      }
      if (hasStoredFormData(this.SESSION_STORAGE_KEY)) {
        if (!this.unloadedSavedDataModal.isOpen) {
          this.unloadedSavedDataModal.open();
        }
      }
      this.formDataLoading = false;

      if (this.formsList) {
        this.restoreLastForm();
      }

      if (
        this.isCoachee &&
        this.currentProformSubmission &&
        this.selectedForm
      ) {
        this.setCoacheeViewed();
      }

      if (this.isCoach) {
        this.getObserveeViews();
      }
    }
  }

  lockForm() {
    this.formLocked = true;
    if (this.selectedForm) {
      StorageService.storeItem(this.LAST_FORM_KEY, {
        formId: this.selectedForm.id,
      });
    }
    this.submitForm();
  }

  unsetForm() {
    if (this.selectedForm) {
      this.selectedForm = null;
      if (this.formSelect) {
        this.formSelect.handleClearClick();
      }
    }
  }

  restoreLastForm() {
    if (this.submissionStatus === FormSubmissionStatus.NEW) {
      // First, check for ProForm submissions in local storage if ProForms are enabled
      if (this.observationProFormsEnabled) {
        const restoredProForm = this.restoreProFormFromLocalStorage();
        if (restoredProForm) {
          return; // ProForm was restored, exit early
        }
      }

      // If no ProForm found, proceed with legacy observation form restoration
      this.lastFormId = getPreviousFormId(this.LAST_FORM_KEY);

      if (this.lastFormId) {
        const formIndex = this.formsList.findIndex(
          (form) => form.id === this.lastFormId
        );

        // Fix: Check for >= 0 instead of truthy, since index 0 is valid
        // Only restore if we have existing submissions (not a brand new log)
        if (formIndex >= 0 && this.submissions && this.submissions.length > 0) {
          this.formSelectValue = this.formsList[formIndex];
          this.updateFormSelect(this.formsList[formIndex]);
        }
      }
    }
  }

  attendeeSelected(user: CoachingSessionAttendeeDTO) {
    this.selectedAttendee = user;
    this.formsService.assessedUser = user.user;
    if (this.submissions && this.submissions.length > 0) {
      const submissionIdx = this.submissions.findIndex(
        (submission) =>
          submission.observedUserId === this.selectedAttendee.userId
      );
      if (submissionIdx > -1) {
        this.currentSubmission = this.submissions[submissionIdx];
        this.selectedForm = this.formsList.find(
          (form) => form.id === this.currentSubmission.formId
        ) as FormOrProForm;
        this.formLocked = true;
        this.submissionStatus = FormSubmissionStatus.SUBMITTED;

        if (this.formView) {
          this.formView.submissionStatus = this.submissionStatus;
          this.formView.submissionData = this.currentSubmission;
        }
        return;
      }
    }
    this.submissionStatus = FormSubmissionStatus.NEW;
    this.createNewSubmission();

    if (this.formView) {
      this.formView.submissionStatus = this.submissionStatus;
      delete this.formView.submissionData;
    }
  }

  createNewSubmission() {
    this.createSubmissionData = {
      formId: this.selectedForm?.id || 0,
      submitterUser: this.user,
      submissionData: [],
      coachingLogId: this.sessionId,
      observedUserId: this.selectedAttendee.userId,
    };
  }

  commitIndicatorDeltas(coachlogId?: number) {
    const logId = coachlogId || this.sessionId;
    if (this.indicatorDeltas.length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      for (const field of this.indicatorDeltas) {
        if (field.assessmentsToRemove.length > 0) {
          // eslint-disable-next-line no-restricted-syntax
          for (const assessment of field.assessmentsToRemove) {
            this.formsService.removeAssessment(assessment);
          }
        }
        if (field.assessmentsToAdd.length > 0) {
          // eslint-disable-next-line no-restricted-syntax
          for (const assessment of field.assessmentsToAdd) {
            if (!assessment.committedToDb) {
              this.formsService.addAssessment({
                ...(assessment.assesmentData as FormLearnerBehaviorCreateDTO),
                coachlogId: logId,
              });
            }
          }
        }
      }
      this.indicatorDeltas = [];
    }
  }

  submitForm(navigateAfterSubmit = false) {
    this.formLocked = true;
    this.formSaving = true;
    this.loadDataIntoCurrentSubmission();
    if (this.isObservationForm(this.selectedForm)) {
      if (this.submissionStatus === FormSubmissionStatus.NEW) {
        this.formsService
          .createFormSubmission(this.createSubmissionData)
          .subscribe((submission) => {
            take(1);
            this.commitIndicatorDeltas(submission.coachingLogId);
            this.submissions.push(submission);
            this.attendeeSelected(this.selectedAttendee);
            this.formsService.repositionFormOnSave = true;
            this.deleteSavedFormData(navigateAfterSubmit);
            this.formSaving = false;
            this.saveError = false;
          });
      } else {
        this.formsService
          .updateFormSubmission(this.currentSubmission)
          .subscribe((submission) => {
            take(1);
            this.commitIndicatorDeltas();
            const idx = this.submissions.findIndex(
              (s) => s.id === submission.id
            );
            if (idx > -1) {
              this.submissions[idx] = submission;
            }
            this.formSaving = false;
            this.deleteSavedFormData(navigateAfterSubmit);
          });
      }
    }
  }

  onProformObservationFormSubmit(submission: ProFormsSubmissionAPIResponse) {
    this.proformSubmissions.push(submission);
    this.loadProformSubmissions();
  }

  loadDataIntoCurrentSubmission() {
    if (hasStoredFormData(this.SESSION_STORAGE_KEY)) {
      const latestData = getSavedFormData(
        this.SESSION_STORAGE_KEY
      ) as ObservationFormSubmissionRawData;
      this.currentSubmission = {
        ...this.currentSubmission,
        ...latestData,
        saveTimestampMS: Date.now(),
      };
      this.updateSubmission(latestData);
    }
  }

  navigatePreviousScreen() {
    this.router.navigate(['../info'], { relativeTo: this.route });
  }

  navigateNextScreen() {
    this.formsSaveService.addToNavigationQueue(this.sessionId, [
      ['../summary'],
      { relativeTo: this.route },
    ]);
    this.submitForm(true);
  }

  updateFormSelect(form: FormOrProForm) {
    this.disableLockFormButton = false;
    this.selectedForm = form;
    if (this.submissionStatus === FormSubmissionStatus.NEW) {
      this.createSubmissionData.formId = form?.id;
    } else {
      this.currentSubmission.formId = form?.id;
    }
  }

  deleteSavedFormData(navigateWithQueue = false): void {
    StorageService.removeItem(this.SESSION_STORAGE_KEY);
    this.formsSaveService.clearNavigationHold(this.sessionId);
    this.unloadedSavedDataModal.close();
    if (navigateWithQueue) {
      this.formsSaveService.navigateFromQueue(this.sessionId);
    }
  }

  loadFormDataForUse(closeModal = false): void {
    if (hasStoredFormData(this.SESSION_STORAGE_KEY)) {
      const latestData = getSavedFormData(
        this.SESSION_STORAGE_KEY
      ) as ObservationFormSubmissionRawData;
      if (!this.selectedForm) {
        const foundForm = this.formsList.find(
          (form) => form.id === latestData.formId
        );
        if (foundForm) {
          this.formSelectValue = foundForm;
          this.updateFormSelect(foundForm);
          this.createSubmissionData.submissionData = (
            latestData as ObservationFormSubmissionRawData
          ).submissionData;
          this.indicatorDeltas = latestData.indicatorDeltas
            ? latestData.indicatorDeltas
            : [];
          this.submitForm();
        } else {
          this.deleteSavedFormData();
        }
      } else {
        this.loadDataIntoCurrentSubmission();
      }
    }
    if (closeModal) {
      this.unloadedSavedDataModal.close();
    }
  }

  updateIndicatorDeltas(indicatorDeltas: CompetencyFieldOutput[]) {
    this.indicatorDeltas = indicatorDeltas;
    const existingData = getSavedFormData(this.SESSION_STORAGE_KEY);
    StorageService.storeItem(this.SESSION_STORAGE_KEY, {
      ...existingData,
      indicatorDeltas,
    });
  }

  updateSubmission(formData: ObservationFormSubmissionRawData) {
    const baseData = this.currentSubmission
      ? this.currentSubmission
      : this.createSubmissionData;
    StorageService.storeItem(this.SESSION_STORAGE_KEY, {
      ...baseData,
      submissionData: {
        ...formData,
        saveTimestampMS: Date.now(),
      },
    });
    this.formsSaveService.addNavigationHold(this.sessionId);
  }

  toggleSmartFeedback(): void {
    this.smartFeedbackActive = !this.smartFeedbackActive;
    this.smartFeedbackTabActive = !this.smartFeedbackTabActive;
  }

  toggleNotes(): void {
    this.notesActive = !this.notesActive;
    this.notesTabActive = !this.notesTabActive;
  }

  toggleComments(): void {
    this.commentsActive = !this.commentsActive;
    this.commentsTabActive = !this.commentsTabActive;
  }

  toggleResources(): void {
    this.resourcesActive = !this.resourcesActive;
    this.resourcesTabActive = !this.resourcesTabActive;
  }

  /**
   * Check if the selected form is a regular observation form (not a ProForm)
   */
  // eslint-disable-next-line class-methods-use-this
  isObservationForm(form: FormOrProForm | null): form is Form {
    return form !== null && isObservationForm(form);
  }

  /**
   * Check if the selected form is a ProForm
   */
  // eslint-disable-next-line class-methods-use-this
  isProForm(form: FormOrProForm | null): form is ProFormsAPIResponse {
    return form !== null && isProForm(form);
  }

  getObservationProForms(observationFormDistrictId: number): void {
    this.proformsFormListService
      .getFormsSimple(
        observationFormDistrictId,
        1000,
        1,
        [ProFormType.OBSERVATION, ProFormType.CALIBRATION],
        ProFormStatus.PUBLISHED
      )
      .subscribe((resp: GetAllProFormsAPIResponse) => {
        if (resp.items) {
          this.formsList = resp.items.sort((a, b) =>
            a.title.localeCompare(b.title)
          );
        }
        this.loadStatus.proforms = true;
        this.loadProformSubmissions();
        this.checkDataLoaded();
      });
  }

  /**
   * Update the cached proform submission when attendee or form changes
   */
  private updateCurrentProformSubmission(): void {
    // Get the most recent submission (by created_at or id)
    this.currentProformSubmission =
      this.proformSubmissions.length > 0
        ? this.proformSubmissions.reduce((latest, current) =>
            current.created_at > latest.created_at ? current : latest
          )
        : null;
    if (this.currentProformSubmission) {
      // Find the corresponding form in our forms list
      this.selectedForm = this.formsList.find(
        (form) => form.id === this.currentProformSubmission?.proform_form_id
      ) as FormOrProForm;

      if (this.selectedForm) {
        this.formLocked = true;
        this.submissionStatus = FormSubmissionStatus.SUBMITTED;
        this.formSelectValue = this.selectedForm;
      }
    }
  }

  /**
   * Load all proform submissions for this coaching log
   */
  private loadProformSubmissions(): void {
    if (!this.formsList || this.formsList.length === 0) {
      return;
    }

    // Load submissions for each proform
    this.subs.push(
      this.proformsSubmissionService
        .getSubmissionsByContext(ProFormContext.COACHLOG, this.sessionId)
        .subscribe({
          next: (response) => {
            if (response.items) {
              this.proformSubmissions = [
                ...this.proformSubmissions,
                ...response.items,
              ];

              // Update the current proform submission ID after loading new submissions
              this.updateCurrentProformSubmission();
            }
          },
          error: () => {
            this.proformSubmissionsLoaded = true;
          },
          complete: () => {
            this.proformSubmissionsLoaded = true;
          },
        })
    );
  }

  /**
   * Check local storage for ProForm submissions and restore form selection
   * Returns true if a ProForm was restored, false otherwise
   */
  restoreProFormFromLocalStorage(): boolean {
    if (!this.formsList || !this.sessionId || !this.user?.id) {
      return false;
    }

    // Check each ProForm in the list for a submission payload in local storage
    const proFormWithSubmission = this.formsList.find((form) => {
      if (isProForm(form)) {
        this.SESSION_STORAGE_KEY = `proform-${form.id}-submission-payload-egrowe_coachlog-${this.sessionId}-user-${this.user.id}-observee-${this.selectedAttendee?.userId}`;
        const storedData = StorageService.getItem(this.SESSION_STORAGE_KEY);
        return storedData;
      }
      return false;
    });

    if (proFormWithSubmission) {
      // Found ProForm with submission data, restore form selection
      this.formSelectValue = proFormWithSubmission;
      this.updateFormSelect(proFormWithSubmission);
      // Simulate the "Select" button click to lock the form
      this.lockForm();
      return true;
    }

    return false;
  }

  setCoacheeViewed() {
    const payload: ObserveeViewPayloadItem = {
      action: 'viewedFormSubmission',
      ref_table: 'egrowe_coachlog',
      ref_table_id: this.sessionId,
    };
    this.coachingService.logObserveeView(payload).subscribe();
  }

  getObserveeViews() {
    this.coachingService
      .checkObserveeViewed(this.sessionId, this.selectedAttendee.userId)
      .subscribe((resp) => {
        if (resp) {
          this.participantView = resp;
        }
      });
  }

  checkDistrictsEmailNotificationStatus(districtId: number) {
    this.districtListService
      .getDistrictObservationFeedbackEmailNotification(districtId)
      .subscribe((response) => {
        this.districtsEmailNotificationsEnabled = response.is_enabled;
      });
  }

  sendObsvEmailNotification() {
    this.processingNotification = true;
    if (this.currentProformSubmission) {
      this.coachingService
        .sendObsvNotification(this.sessionData.id)
        .subscribe((resp) => {
          if (resp) {
            setTimeout(() => {
              this.processingNotification = false;
              this.alertServce.showAlert('Email has been sent');
            }, 1000);
          }
        });
    }
  }
}
